import { useDisclosure } from '@chakra-ui/react';
import { IContactRespSchema } from '@monetize/types/app';
import {
  DEFAULT_API_RESPONSE,
  DEFAULT_PAGER,
  FilterType,
  TDataTablePager,
} from '@monetize/types/common';
import { useMemo, useState } from 'react';
import { useGetAccountContacts } from '../../api/contactsService';
import {
  buildFilterParamsRequestObject,
  transformTableFilterValue,
} from '../../utils';

export const useContacts = (accountId: string) => {
  const [filters, setFilters] = useState<FilterType[]>([]);
  // might be dynamic in the future. Keep static for now
  const [searchKey] = useState<string>('fullName');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pager, setPager] = useState<TDataTablePager>(DEFAULT_PAGER);

  const filterParams = useMemo(
    () =>
      buildFilterParamsRequestObject(
        transformTableFilterValue(filters),
        searchTerm,
        searchKey,
      ),
    [filters, searchTerm, searchKey],
  );

  const {
    open: isModalOpen,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const {
    isLoading,
    isFetched,
    isRefetching,
    data: listData,
    isError,
    error,
    refetch,
  } = useGetAccountContacts(
    {
      accountId,
      config: pager,
      filters: filterParams,
    },
    { meta: { showErrorToast: true } },
  );

  const [selected, setSelected] = useState<null | IContactRespSchema>(null);

  const openCreateModal = () => {
    onOpenModal();
  };

  const openEditModal = (row: IContactRespSchema) => {
    setSelected(row);
    onOpenModal();
  };

  const onResetFilter = () => {
    setFilters([]);
    setPager(DEFAULT_PAGER);
  };

  return {
    pager,
    setPager,
    filters,
    setFilters,
    onResetFilter,
    searchKey,
    searchTerm,
    setSearchTerm,
    totalRecords: listData?.totalElements || 0,
    loading: isLoading || (isRefetching && !isFetched),
    isModalOpen,
    onOpenModal,
    onCloseModal: () => {
      setSelected(null);
      onCloseModal();
    },
    openCreateModal,
    openEditModal,
    listData: listData || DEFAULT_API_RESPONSE,
    selected,
    setSelected,
    isError,
    error,
    refetch,
  };
};
